import { useCallback } from 'react';
import { Task, FullProject } from '../../../types';
import { db } from '../../../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { shouldAcceptProjectSnapshot, getCollaborativeProjectIds } from './projectMergeEngine';

export interface UseProjectManualFetchProps {
  userId?: string;
  user: { uid?: string; email?: string | null; displayName?: string | null } | null;
  tenantId: string;
  INBOX_PROJECT_ID: string;
  COMM_PROJECT_ID: string;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  setIsSyncing: React.Dispatch<React.SetStateAction<boolean>>;
  setLastSynced: React.Dispatch<React.SetStateAction<number | null>>;
  setSyncError: React.Dispatch<React.SetStateAction<string | null>>;
}

export function useProjectManualFetch({
  userId,
  user,
  tenantId,
  INBOX_PROJECT_ID,
  COMM_PROJECT_ID,
  setAllProjects,
  setIsSyncing,
  setLastSynced,
  setSyncError,
}: UseProjectManualFetchProps) {
  const fetchProjectsFromFirestore = useCallback(async () => {
    try {
      if (!userId || userId === 'e2e_local_uid') {
        if (!userId) console.warn('[ProjectManager] fetchProjectsFromFirestore: userId is undefined');
        return [];
      }
      const userEmail = user?.email?.toLowerCase() || '';
      const isAdmin = false;
      const fetchedProjectsMap = new Map<string, FullProject>();

      const addFetchedProjects = (docs: QueryDocumentSnapshot<DocumentData>[]) => {
        docs.forEach(docSnap => {
          const data = docSnap.data();
          const fp = { ...data, id: docSnap.id } as FullProject;
          if (!shouldAcceptProjectSnapshot(fp, { userId, tenantId, INBOX_PROJECT_ID, COMM_PROJECT_ID })) {
            return;
          }
          fetchedProjectsMap.set(fp.id, fp);
        });
      };

      if (isAdmin) {
        const qAll = query(collection(db, 'projects'), where('tenantId', '==', tenantId));
        const snap = await getDocs(qAll);
        addFetchedProjects(snap.docs);
      } else {
        const qOwned = query(collection(db, 'projects'), where('userId', '==', userId));
        const snapOwned = await getDocs(qOwned);
        addFetchedProjects(snapOwned.docs);

        const qPublic = query(
          collection(db, 'projects'),
          where('tenantId', '==', tenantId),
          where('visibility', '==', 'public')
        );
        const snapPublic = await getDocs(qPublic);
        addFetchedProjects(snapPublic.docs);

        const qSample = query(
          collection(db, 'projects'),
          where('tenantId', '==', tenantId),
          where('isSample', '==', true)
        );
        const snapSample = await getDocs(qSample);
        addFetchedProjects(snapSample.docs);
      }

      if (userEmail) {
        try {
          const qSharedViewer = query(collection(db, 'projects'), where('sharedWith', 'array-contains', userEmail));
          const snapSharedViewer = await getDocs(qSharedViewer);
          addFetchedProjects(snapSharedViewer.docs);

          const qSharedEditor = query(collection(db, 'projects'), where('sharedEditors', 'array-contains', userEmail));
          const snapSharedEditor = await getDocs(qSharedEditor);
          addFetchedProjects(snapSharedEditor.docs);
        } catch (sharedErr) {
          console.warn('[ProjectManager] Failed to fetch shared projects:', sharedErr);
        }
      }

      const fetchedProjects = Array.from(fetchedProjectsMap.values());

      try {
        const inboxRef = doc(db, 'projects', INBOX_PROJECT_ID);
        const inboxSnap = await getDoc(inboxRef);
        if (inboxSnap.exists() && !fetchedProjectsMap.has(INBOX_PROJECT_ID)) {
          fetchedProjects.push({
            ...inboxSnap.data(),
            id: INBOX_PROJECT_ID,
          } as FullProject);
        }

        if (INBOX_PROJECT_ID !== 'inbox_inforum_' + tenantId) {
          const globalInboxRef = doc(db, 'projects', 'inbox_inforum_' + tenantId);
          const globalInboxSnap = await getDoc(globalInboxRef);
          if (globalInboxSnap.exists() && !fetchedProjectsMap.has('inbox_inforum_' + tenantId)) {
            fetchedProjects.push({
              ...globalInboxSnap.data(),
              id: 'inbox_inforum_' + tenantId,
            } as FullProject);
          }
        }
      } catch (inboxErr) {
        console.error('[ProjectManager] Failed to load user inbox:', inboxErr);
      }

      let fetchedTasks: Task[] = [];
      try {
        const qTasks = isAdmin
          ? query(collection(db, 'tasks'), where('tenantId', '==', tenantId))
          : query(collection(db, 'tasks'), where('userId', '==', userId));
        const tasksSnapshot = await getDocs(qTasks);
        fetchedTasks = tasksSnapshot.docs.map(docSnap => ({
          ...docSnap.data(),
          id: docSnap.id,
        })) as Task[];
      } catch (tasksErr) {
        console.error('[ProjectManager] Failed to fetch tasks from Firestore:', tasksErr);
      }

      const sharedProjIds = getCollaborativeProjectIds(fetchedProjects, userId, INBOX_PROJECT_ID);

      if (sharedProjIds.length > 0) {
        try {
          const chunks: string[][] = [];
          for (let i = 0; i < sharedProjIds.length; i += 30) {
            chunks.push(sharedProjIds.slice(i, i + 30));
          }

          for (const chunk of chunks) {
            const qSharedTasks = query(collection(db, 'tasks'), where('projectId', 'in', chunk));
            const sharedTasksSnapshot = await getDocs(qSharedTasks);
            sharedTasksSnapshot.docs.forEach(docSnap => {
              const t = {
                ...docSnap.data(),
                id: docSnap.id,
              } as Task;
              if (!fetchedTasks.some(existingTask => existingTask.id === t.id)) {
                fetchedTasks.push(t);
              }
            });
          }
        } catch (sharedTasksErr) {
          console.error('[ProjectManager] Failed to fetch tasks for shared projects:', sharedTasksErr);
        }
      }

      const tasksByProjectId = new Map<string, Task[]>();
      fetchedTasks.forEach(t => {
        const pId = t.projectId || 'default';
        if (!tasksByProjectId.has(pId)) {
          tasksByProjectId.set(pId, []);
        }
        tasksByProjectId.get(pId)!.push(t);
      });

      const mergedProjects = fetchedProjects.map(proj => {
        const normalizedTasks = tasksByProjectId.get(proj.id) || [];
        const tasks = normalizedTasks.length > 0 ? normalizedTasks : proj.tasks || [];

        return {
          ...proj,
          tasks: [...tasks].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
        } as FullProject;
      });

      mergedProjects.sort((a, b) => (b.lastModified || 0) - (a.lastModified || 0));
      return mergedProjects;
    } catch (err) {
      console.error('Failed to fetch projects from Firestore:', err);
      return null;
    }
  }, [userId, user?.email, tenantId, INBOX_PROJECT_ID, COMM_PROJECT_ID]);

  const syncInboxForce = useCallback(async () => {
    if (userId === 'e2e_local_uid') {
      setLastSynced(Date.now());
      return;
    }
    try {
      setIsSyncing(true);
      const docRef = doc(db, 'projects', INBOX_PROJECT_ID);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        let inboxTasks: Task[] = [];
        try {
          const qInboxTasks = query(collection(db, 'tasks'), where('projectId', '==', INBOX_PROJECT_ID));
          const tasksSnap = await getDocs(qInboxTasks);
          inboxTasks = tasksSnap.docs.map(docItem => ({
            ...docItem.data(),
            id: docItem.id,
          })) as Task[];
        } catch (tasksErr) {
          console.error('[Sync] Failed to fetch inbox tasks:', tasksErr);
        }

        const freshInboxProject = {
          ...docSnap.data(),
          id: 'inbox_inforum',
          tasks: inboxTasks.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
        } as FullProject;

        setAllProjects(prev => {
          const idx = prev.findIndex(p => p.id === 'inbox_inforum');
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = freshInboxProject;
            return next;
          }
          return [...prev, freshInboxProject];
        });
        setLastSynced(Date.now());
      }
    } catch (err) {
      console.error('Failed to force sync inbox_inforum:', err);
      setSyncError('Konnte Posteingang nicht laden');
    } finally {
      setIsSyncing(false);
    }
  }, [userId, INBOX_PROJECT_ID, setAllProjects, setIsSyncing, setLastSynced, setSyncError]);

  return {
    fetchProjectsFromFirestore,
    syncInboxForce,
  };
}
